import React, { useState, useEffect } from 'react';
import { 
  Plus, Search, Edit, Trash2, Eye, User, Users, Mail, Phone, 
  MapPin, Building, CreditCard, ChevronLeft, ChevronRight, 
  ChevronUp, ChevronDown, AlertTriangle,
  X, AlertCircle, CheckCircle, XCircle, Filter, Download, 
  Upload, Calendar, FileText, TrendingUp,
  Clock, Settings 
} from 'lucide-react';
import { clientesService } from '../../../services/api';

const FeedbackModal = ({ isOpen, type, title, message, onConfirm, onClose, showCancel }) => {
  if (!isOpen) return null;

  const icons = {
    success: <CheckCircle className="text-green-500 w-12 h-12" />,
    error: <XCircle className="text-red-500 w-12 h-12" />,
    warning: <AlertTriangle className="text-yellow-500 w-12 h-12" />,
    info: <AlertCircle className="text-blue-500 w-12 h-12" />
  };

  const colors = {
    success: 'bg-green-600 hover:bg-green-700',
    error: 'bg-red-600 hover:bg-red-700',
    warning: 'bg-yellow-600 hover:bg-yellow-700',
    info: 'bg-blue-600 hover:bg-blue-700'
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6 text-center transform transition-all">
        <div className="flex justify-center mb-4">
          {icons[type] || icons.info}
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-6">{message}</p>
        <div className="flex justify-center gap-3">
          {showCancel && (
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
          )}
          <button
            onClick={() => {
              if (onConfirm) onConfirm();
              onClose();
            }}
            className={`px-4 py-2 text-white rounded-lg transition-colors ${colors[type] || colors.info}`}
          >
            Aceptar
          </button>
        </div>
      </div>
    </div>
  );
};

const ClientesView = () =>  {
  const [clientes, setClientes] = useState([]);
  const [clientesFiltrados, setClientesFiltrados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Estados para búsqueda y paginación
  const [searchTerm, setSearchTerm] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('nombre');
  const [sortDirection, setSortDirection] = useState('asc');
  const [expandedCards, setExpandedCards] = useState({});
  const [feedback, setFeedback] = useState({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null, showCancel: false });

  const showFeedback = (type, title, message, onConfirm = null, showCancel = false) => {
    setFeedback({ isOpen: true, type, title, message, onConfirm, showCancel });
  };

  const closeFeedback = () => setFeedback(prev => ({ ...prev, isOpen: false }));

  const toggleCardExpansion = (clienteId) => {
    setExpandedCards(prev => ({
        ...prev,
        [clienteId]: !prev[clienteId]
    }));
  };

  // Cargar clientes
  const cargarClientes = async () => {
    try {
      setLoading(true);
      const response = await clientesService.getAll({});
      setClientes(response.data);
      setClientesFiltrados(response.data);
    } catch (error) {
      console.error('Error al cargar clientes:', error);
      showFeedback('error', 'Error', error.message || 'Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  // Cargar estadísticas
  const cargarStats = async () => {
    try {
      const response = await clientesService.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  };

  useEffect(() => {
    cargarClientes();
    cargarStats();
  }, []);

  // Filtrar y buscar
  useEffect(() => {
    let resultado = [...clientes];

    // Filtrar por tipo
    if (tipoFiltro) {
      resultado = resultado.filter(c => c.tipo_persona === tipoFiltro);
    }

    // Buscar
    if (searchTerm) {
      resultado = resultado.filter(c =>
        c.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.cedula && c.cedula.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (c.rif && c.rif.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (c.email && c.email.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Ordenar
    resultado.sort((a, b) => {
      let aVal = a[sortColumn] || '';
      let bVal = b[sortColumn] || '';
      
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setClientesFiltrados(resultado);
    setCurrentPage(1);
  }, [searchTerm, tipoFiltro, clientes, sortColumn, sortDirection]);

  // Paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = clientesFiltrados.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(clientesFiltrados.length / itemsPerPage);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleNuevoCliente = () => {
    setModalMode('create');
    setClienteSeleccionado(null);
    setShowModal(true);
  };

  const handleEditarCliente = (cliente) => {
    setModalMode('edit');
    setClienteSeleccionado(cliente);
    setShowModal(true);
  };

  const handleEliminarCliente = (id) => {
    showFeedback('warning', 'Confirmar Eliminación', '¿Estás seguro de eliminar este cliente?', async () => {
      try {
        await clientesService.delete(id);
        showFeedback('success', 'Éxito', 'Cliente eliminado exitosamente');
        cargarClientes();
        cargarStats();
      } catch (error) {
        showFeedback('error', 'Error', error.message || 'Error al eliminar cliente');
      }
    }, true);
  };

  const handleVerCliente = (cliente) => {
    setModalMode('view');
    setClienteSeleccionado(cliente);
    setShowModal(true);
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl lg:text-2xl font-bold text-gray-800">Gestión de Clientes</h2>
          <p className="text-sm lg:text-base text-gray-600">Administra tu cartera de clientes</p>
        </div>
        <button 
          onClick={handleNuevoCliente}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 hover:bg-blue-700 w-full sm:w-auto"
        >
          <Plus size={20} /> Nuevo Cliente
        </button>
      </div>

      {/* Estadísticas */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-xs lg:text-sm">Total Clientes</p>
                <p className="text-xl lg:text-2xl font-bold text-gray-800">{stats.total}</p>
              </div>
              <Users className="text-blue-500" size={28} />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-xs lg:text-sm">Personas Naturales</p>
                <p className="text-xl lg:text-2xl font-bold text-gray-800">{stats.naturales}</p>
              </div>
              <Users className="text-green-500" size={28} />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-xs lg:text-sm">Personas Jurídicas</p>
                <p className="text-xl lg:text-2xl font-bold text-gray-800">{stats.juridicas}</p>
              </div>
              <Users className="text-purple-500" size={28} />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-xs lg:text-sm">Activos</p>
                <p className="text-xl lg:text-2xl font-bold text-gray-800">{stats.activos}</p>
              </div>
              <CheckCircle className="text-green-500" size={28} />
            </div>
          </div>
        </div>
      )}

      {/* Filtros y búsqueda */}
      <div className="bg-white rounded-lg shadow mb-4 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
          <input 
            type="text" 
            placeholder="Buscar por nombre, cédula, RIF, email..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <select 
            value={tipoFiltro}
            onChange={(e) => setTipoFiltro(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">Todos los tipos</option>
            <option value="natural">Persona Natural</option>
            <option value="juridica">Persona Jurídica</option>
          </select>
          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value={10}>10 por página</option>
            <option value={25}>25 por página</option>
            <option value={50}>50 por página</option>
            <option value={100}>100 por página</option>
          </select>
        </div>
      </div>

      {/* Layout Unificado - Tarjetas */}
      <div className="space-y-4">
        {loading ? (
          <div className="p-12 text-center bg-white rounded-lg shadow">
            <p className="text-gray-600">Cargando clientes...</p>
          </div>
        ) : clientesFiltrados.length === 0 ? (
          <div className="p-12 text-center bg-white rounded-lg shadow">
            <Users size={64} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">No se encontraron clientes</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {currentItems.map(cliente => (
              <div key={cliente.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow border border-gray-200">
                {/* Header de la tarjeta */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 rounded-t-lg border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 flex-shrink-0">
                      {cliente.tipo_persona === 'natural' ? <User size={20} /> : <Building size={20} />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-lg text-blue-600">{cliente.nombre}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          cliente.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {cliente.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                          cliente.tipo_persona === 'natural' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'
                        }`}>
                          {cliente.tipo_persona === 'natural' ? 'Natural' : 'Jurídica'}
                        </span>
                        <span className="text-sm font-medium text-gray-600">
                          {cliente.tipo_persona === 'natural' ? cliente.cedula : cliente.rif}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => toggleCardExpansion(cliente.id)} className="text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors" title={expandedCards[cliente.id] ? "Contraer detalles" : "Expandir detalles"}>
                      {expandedCards[cliente.id] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                    <button onClick={() => handleVerCliente(cliente)} className="text-blue-600 hover:text-blue-800 hover:bg-blue-100 p-2 rounded-lg transition-colors" title="Ver detalles"><Eye size={20} /></button>
                    <button onClick={() => handleEditarCliente(cliente)} className="text-green-600 hover:text-green-800 hover:bg-green-100 p-2 rounded-lg transition-colors" title="Editar"><Edit size={20} /></button>
                    <button onClick={() => handleEliminarCliente(cliente.id)} className="text-red-600 hover:text-red-800 hover:bg-red-100 p-2 rounded-lg transition-colors" title="Eliminar"><Trash2 size={20} /></button>
                  </div>
                </div>

                {/* Contenido colapsado/expandido */}
                <div className="p-4">
                  {!expandedCards[cliente.id] ? (
                    // Vista resumida
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Teléfono</p>
                        <p className="text-sm text-gray-800">{cliente.telefono || <span className="text-gray-400">N/A</span>}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Email</p>
                        <p className="text-sm text-gray-800 truncate" title={cliente.email}>{cliente.email || <span className="text-gray-400">N/A</span>}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Agente Retención</p>
                        <p className="text-sm text-gray-800">
                          {cliente.agente_retencion ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-800">
                              Sí ({(cliente.porcentaje_retencion || 0) * 100}%)
                            </span>
                          ) : <span className="text-gray-500">No</span>}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Dirección</p>
                        <p className="text-sm text-gray-800 truncate" title={cliente.direccion}>{cliente.direccion || <span className="text-gray-400">N/A</span>}</p>
                      </div>
                    </div>
                  ) : (
                    // Vista expandida detallada
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg border border-gray-100">
                      <div className="flex items-start gap-3">
                        <Phone className="text-gray-400 mt-0.5" size={16} />
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Teléfono</p>
                          <p className="text-sm text-gray-800">{cliente.telefono || <span className="text-gray-400">No registrado</span>}</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <Mail className="text-gray-400 mt-0.5" size={16} />
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Email</p>
                          <p className="text-sm text-gray-800">{cliente.email || <span className="text-gray-400">No registrado</span>}</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <MapPin className="text-gray-400 mt-0.5" size={16} />
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Dirección</p>
                          <p className="text-sm text-gray-800">{cliente.direccion || <span className="text-gray-400">No registrada</span>}</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <CreditCard className="text-gray-400 mt-0.5" size={16} />
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Identificación</p>
                          <p className="text-sm text-gray-800">{cliente.tipo_persona === 'natural' ? `C.I. ${cliente.cedula}` : `RIF ${cliente.rif}`}</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <FileText className="text-gray-400 mt-0.5" size={16} />
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Agente de Retención</p>
                          <p className="text-sm text-gray-800">
                            {cliente.agente_retencion ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-800">
                                Retención del {(cliente.porcentaje_retencion || 0) * 100}%
                              </span>
                            ) : <span className="text-gray-500 font-medium">No</span>}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <Calendar className="text-gray-400 mt-0.5" size={16} />
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Fecha de Registro</p>
                          <p className="text-sm text-gray-800">
                            {cliente.created_at ? new Date(cliente.created_at).toLocaleDateString('es-ES') : <span className="text-gray-400">Desconocida</span>}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Paginación unificada */}
        {clientesFiltrados.length > 0 && (
          <div className="bg-white rounded-lg shadow p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-700 text-center sm:text-left">
              Mostrando {indexOfFirstItem + 1} a {Math.min(indexOfLastItem, clientesFiltrados.length)} de {clientesFiltrados.length} registros
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                Primero
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                Anterior
              </button>
              <span className="px-3 py-1 text-sm font-medium">
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                Siguiente
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                Último
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <ClienteModal
          mode={modalMode}
          cliente={clienteSeleccionado}
          onClose={() => {
            setShowModal(false);
            setClienteSeleccionado(null);
          }}
          onSuccess={(mensaje) => {
            setShowModal(false);
            setClienteSeleccionado(null);
            cargarClientes();
            cargarStats();
            if (mensaje) {
              showFeedback('success', 'Éxito', mensaje);
            }
          }}
          showFeedback={showFeedback}
        />
      )}

      <FeedbackModal
        {...feedback}
        onClose={closeFeedback}
      />
    </div>
  );
};

// Modal de Cliente
  const ClienteModal = ({ mode, cliente, onClose, onSuccess, showFeedback }) => {
  const [formData, setFormData] = useState({
    tipo_persona: 'natural',
    nombre: '',
    cedula: '',
    rif: '',
    telefono: '',
    email: '',
    direccion: '',
    agente_retencion: false,
    porcentaje_retencion: 0.75,
    activo: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (cliente) {
      setFormData({
        tipo_persona: cliente.tipo_persona,
        nombre: cliente.nombre,
        cedula: cliente.cedula || '',
        rif: cliente.rif || '',
        telefono: cliente.telefono || '',
        email: cliente.email || '',
        direccion: cliente.direccion || '',
        agente_retencion: cliente.agente_retencion || false,
        porcentaje_retencion: cliente.porcentaje_retencion || 0.75,
        activo: cliente.activo !== undefined ? cliente.activo : true
      });
    }
  }, [cliente]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (mode === 'create') {
        await clientesService.create(formData);
        onSuccess('Cliente creado exitosamente');
      } else if (mode === 'edit') {
        await clientesService.update(cliente.id, formData);
        onSuccess('Cliente actualizado exitosamente');
      }
    } catch (err) {
      setError(err.message || 'Error al guardar cliente');
    } finally {
      setLoading(false);
    }
  };

  const isViewMode = mode === 'view';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-800">
              {mode === 'create' && 'Nuevo Cliente'}
              {mode === 'edit' && 'Editar Cliente'}
              {mode === 'view' && 'Detalles del Cliente'}
            </h3>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <span className="text-2xl">×</span>
            </button>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <div className="space-y-4">
            {/* Tipo de Persona */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Tipo de Persona <span className="text-red-500">*</span>
              </label>
              <select
                name="tipo_persona"
                value={formData.tipo_persona}
                onChange={handleChange}
                disabled={isViewMode}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="natural">Persona Natural</option>
                <option value="juridica">Persona Jurídica</option>
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Nombre */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  {formData.tipo_persona === 'natural' ? 'Nombre complero' : 'Razón Social'} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  disabled={isViewMode}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  placeholder={formData.tipo_persona === 'natural' ? 'Ej: Juan' : 'Ej: Empresa ABC C.A.'}
                />
              </div>


             
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Cédula o RIF */}
              {formData.tipo_persona === 'natural' ? (
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Cédula <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="cedula"
                    value={formData.cedula}
                    onChange={handleChange}
                    disabled={isViewMode}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    placeholder="Ej: V-12345678"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    RIF <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="rif"
                    value={formData.rif}
                    onChange={handleChange}
                    disabled={isViewMode}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    placeholder="Ej: J-123456789"
                  />
                </div>
              )}

              {/* Teléfono */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">Teléfono</label>
                <input
                  type="text"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  disabled={isViewMode}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  placeholder="Ej: 414-1234567"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">Correo Electrónico</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                disabled={isViewMode}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="Ej: cliente@email.com"
              />
            </div>

            {/* Dirección */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">Dirección</label>
              <textarea
                name="direccion"
                value={formData.direccion}
                onChange={handleChange}
                disabled={isViewMode}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="Dirección completa del cliente"
              />
            </div>

            {/* Retención */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center mt-6">
                <input
                  type="checkbox"
                  name="agente_retencion"
                  checked={formData.agente_retencion}
                  onChange={handleChange}
                  disabled={isViewMode}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label className="ml-2 text-gray-700 font-medium">Es Agente de Retención</label>
              </div>

              {formData.agente_retencion && (
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Porcentaje de Retención</label>
                  <select
                    name="porcentaje_retencion"
                    value={formData.porcentaje_retencion}
                    onChange={handleChange}
                    disabled={isViewMode}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  >
                    <option value={0.75}>75%</option>
                    <option value={1}>100%</option>
                  </select>
                </div>
              )}
            </div>

            {/* Estado (solo en edición) */}
            {mode === 'edit' && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="activo"
                  checked={formData.activo}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label className="ml-2 text-gray-700 font-medium">Cliente Activo</label>
              </div>
            )}

            {/* Información adicional en modo vista */}
            {isViewMode && cliente && (
              <div className="border-t pt-4 mt-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Creado por:</p>
                    <p className="font-semibold">{cliente.creador_nombre || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Fecha de creación:</p>
                    <p className="font-semibold">
                      {new Date(cliente.created_at).toLocaleDateString('es-ES')}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Agente de Retención:</p>
                    <p className={`font-semibold ${cliente.agente_retencion ? 'text-orange-600' : 'text-gray-800'}`}>
                      {cliente.agente_retencion ? `Sí (${(cliente.porcentaje_retencion || 0) * 100}%)` : 'No'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end gap-4">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {isViewMode ? 'Cerrar' : 'Cancelar'}
          </button>
          {!isViewMode && (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className={`px-6 py-2 rounded-lg text-white ${
                loading 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading ? 'Guardando...' : 'Guardar'}
            </button>
          )}
        </div>
      </div>
    </div>
    );
  };

export default ClientesView;